import { useEffect, useState } from "react";
import { ApiRequest } from "GlobalFunctions";
import Box from "elements/Box";
import { Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AlertDialog from "elements/AlertDialog";
import CompaniesEditor from "./CompaniesEditor";
import { useSnackbar } from "notistack";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from '@mui/icons-material/Add';

export default function CompaniesList(props: any) {
  const [list, setList] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [activeCompany, setActiveCompany] = useState<any>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadList = async () => {
    setLoading(true);
    await ApiRequest({
      url: '/companies/list',
      method: 'GET',
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setList(response.list);
          // If your API supports pagination, you should set total pages here
          setTotalPages(Math.ceil(response.list.length / 10)); // Assuming 10 items per page
        } else {
          enqueueSnackbar('Error al cargar las empresas', { variant: 'error' });
        }
      }
    });
    setLoading(false);
  };

  useEffect(() => {
    loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (company_id: number) => {
    setActiveCompany(company_id);
    setOpenEditor(true);
  };

  const handleDelete = (company_id: number) => {
    setActiveCompany(company_id);
    setOpenAlert(true);
  };

  const deleteCompany = async () => {
    await ApiRequest({
      url: '/companies/delete',
      method: 'POST',
      body: { company_id: activeCompany },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          enqueueSnackbar('Empresa eliminada correctamente', { variant: 'success' });
          loadList();
          setOpenAlert(false);
        } else {
          enqueueSnackbar('Error al eliminar empresa', { variant: 'error' });
        }
      }
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Calculate items for current page (this is a client-side implementation)
  const itemsPerPage = 10;
  const startIndex = (page - 1) * itemsPerPage;
  const visibleRows = list.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="content">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Empresas</h2>
            <span className="h2_subtitle">{list.length} empresas registradas</span>
          </div>
          
          <div className="boxContainerActions">
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setActiveCompany(null);
                setOpenEditor(true);
              }}
              startIcon={<AddIcon />}
            >
              Agregar Empresa
            </Button>
          </div>
        </div>

        <Box id='AdminList' className='box100'>
          {loading ? (
            <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <CircularProgress />
            </div>
          ) : (
            <TableContainer>
              <Table aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{color:'#a8a8a8', borderBottom:"none"}}>Logo</TableCell>
                    <TableCell sx={{color:'#a8a8a8', borderBottom:"none"}}>Nombre</TableCell>
                    <TableCell sx={{color:'#a8a8a8', borderBottom:"none"}}>Descripción</TableCell>
                    <TableCell sx={{color:'#a8a8a8', borderBottom:"none"}} align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length > 0 ? (
                    visibleRows.map((row: any) => (
                      <TableRow key={row.company_id} hover>
                        <TableCell>
                          {row.logo ? (
                            <img src={row.logo} alt="Logo" style={{ height: '40px', maxWidth: '100px' }} />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">
                          <Button 
                            size="small"
                            variant="contained"
                            color="primary"
                            style={{"marginRight": "10px"}}
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit(row.company_id)}
                          >
                            Editar
                          </Button>
                          <IconButton 
                            aria-label="delete" 
                            color="error"
                            onClick={() => handleDelete(row.company_id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No hay empresas disponibles
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalPages > 1 && (
            <Pagination 
              count={totalPages} 
              page={page}  
              onChange={handlePageChange} 
              color="primary"
              style={{"marginTop": "20px","alignSelf": "center"}}
            />
          )}
        </Box>
      </div>

      <CompaniesEditor
        open={openEditor}
        setOpen={setOpenEditor}
        company_id={activeCompany}
        loadList={loadList}
      />

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="¿Está seguro que desea eliminar esta empresa?"
        send={deleteCompany}
      />
    </div>
  );
}
